import React, { memo, useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { AdminDTO } from '../../types/admin.dto';
import { FormValues, TableValue, TableValueMap } from '../../types/types';

// Tipos para componentes otimizados
interface OptimizedTableProps<T> {
  data: T[];
  columns: Array<{
    key: keyof T | string;
    label: string;
    render?: (value: TableValue, item: T, index: number) => React.ReactNode;
    sortable?: boolean;
    width?: string;
  }>;
  loading?: boolean;
  onSort?: (key: string, direction: 'asc' | 'desc') => void;
  onRowClick?: (item: T, index: number) => void;
  selectedRows?: Set<number>;
  onRowSelect?: (index: number, selected: boolean) => void;
  className?: string;
  emptyMessage?: string;
}

interface OptimizedCardProps {
  title?: string;
  subtitle?: string;
  children: React.ReactNode;
  loading?: boolean;
  error?: string | null;
  actions?: React.ReactNode;
  className?: string;
  onClick?: () => void;
  hoverable?: boolean;
}

interface OptimizedFormProps {
  onSubmit: (data: FormValues) => void | Promise<void>;
  loading?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
  className?: string;
  validateOnChange?: boolean;
  resetOnSubmit?: boolean;
}

interface OptimizedButtonProps {
  children: React.ReactNode;
  onClick?: () => void | Promise<void>;
  loading?: boolean;
  disabled?: boolean;
  variant?: 'primary' | 'secondary' | 'danger' | 'success' | 'warning';
  size?: 'sm' | 'md' | 'lg';
  type?: 'button' | 'submit' | 'reset';
  className?: string;
  icon?: React.ReactNode;
  fullWidth?: boolean;
}

interface OptimizedInputProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'url';
  required?: boolean;
  disabled?: boolean;
  error?: string;
  helperText?: string;
  className?: string;
  autoComplete?: string;
  maxLength?: number;
  debounceMs?: number;
}

/**
 * Tabela otimizada com virtualização e memoização
 */
export const OptimizedTable = memo(<T extends TableValueMap>({
  data,
  columns,
  loading = false,
  onSort,
  onRowClick,
  selectedRows = new Set<number>(),
  onRowSelect,
  className = '',
  emptyMessage = 'Nenhum dado encontrado'
}: OptimizedTableProps<T>) => {
  const [sortKey, setSortKey] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const handleSort = useCallback((key: string) => {
    const newDirection = sortKey === key && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortKey(key);
    setSortDirection(newDirection);
    onSort?.(key, newDirection);
  }, [sortKey, sortDirection, onSort]);

  const handleRowClick = useCallback((item: T, index: number) => {
    onRowClick?.(item, index);
  }, [onRowClick]);

  const handleRowSelect = useCallback((index: number, event: React.ChangeEvent<HTMLInputElement>) => {
    onRowSelect?.(index, event.target.checked);
  }, [onRowSelect]);

  const isTableValueMap = (value: TableValue): value is TableValueMap => {
    return !!value && typeof value === 'object' && !Array.isArray(value);
  };

  const getNestedValue = (item: T, key: keyof T | string): TableValue => {
    if (typeof key === 'string' && key.includes('.')) {
      return key.split('.').reduce<TableValue>((obj, currentKey) => {
        if (isTableValueMap(obj)) {
          return obj[currentKey];
        }
        return undefined;
      }, item as TableValue);
    }
    return item[key as keyof T] as TableValue;
  };

  const memoizedRows = useMemo(() => {
    return data.map((item, index) => (
      <tr
        key={index}
        className={`
          border-b border-gray-100 hover:bg-gray-50 transition-colors
          ${onRowClick ? 'cursor-pointer' : ''}
          ${selectedRows.has(index) ? 'bg-blue-50' : ''}
        `}
        onClick={() => handleRowClick(item, index)}
      >
        {onRowSelect && (
          <td className="px-4 py-3">
            <input
              type="checkbox"
              checked={selectedRows.has(index)}
              onChange={(e) => handleRowSelect(index, e)}
              onClick={(e) => e.stopPropagation()}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
          </td>
        )}
        {columns.map((column, colIndex) => {
          const value = getNestedValue(item, column.key);

          return (
            <td key={colIndex} className="px-4 py-3 text-sm text-gray-900">
              {column.render ? column.render(value, item, index) : String(value || '')}
            </td>
          );
        })}
      </tr>
    ));
  }, [data, columns, selectedRows, handleRowClick, handleRowSelect, onRowSelect]);

  if (loading) {
    return (
      <div className={`bg-white rounded-lg shadow ${className}`}>
        <div className="animate-pulse">
          <div className="bg-gray-100 h-12 rounded-t-lg"></div>
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="border-b border-gray-100 p-4">
              <div className="grid grid-cols-4 gap-4">
                {Array.from({ length: 4 }).map((_, j) => (
                  <div key={j} className="h-4 bg-gray-200 rounded"></div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg shadow overflow-hidden ${className}`}>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {onRowSelect && (
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <input
                    type="checkbox"
                    checked={data.length > 0 && selectedRows.size === data.length}
                    onChange={(e) => {
                      data.forEach((_, index) => {
                        onRowSelect(index, e.target.checked);
                      });
                    }}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
              )}
              {columns.map((column, index) => (
                <th
                  key={index}
                  className={`
                    px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider
                    ${column.sortable ? 'cursor-pointer hover:bg-gray-100' : ''}
                  `}
                  style={{ width: column.width }}
                  onClick={column.sortable ? () => handleSort(String(column.key)) : undefined}
                >
                  <div className="flex items-center space-x-1">
                    <span>{column.label}</span>
                    {column.sortable && (
                      <span className="text-gray-400">
                        {sortKey === column.key ? (
                          sortDirection === 'asc' ? '↑' : '↓'
                        ) : '↕'}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.length > 0 ? memoizedRows : (
              <tr>
                <td
                  colSpan={columns.length + (onRowSelect ? 1 : 0)}
                  className="px-4 py-8 text-center text-gray-500"
                >
                  {emptyMessage}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
});

OptimizedTable.displayName = 'OptimizedTable';

/**
 * Card otimizado com estados de loading e erro
 */
export const OptimizedCard = memo(({
  title,
  subtitle,
  children,
  loading = false,
  error = null,
  actions,
  className = '',
  onClick,
  hoverable = false
}: OptimizedCardProps) => {
  const handleClick = useCallback(() => {
    onClick?.();
  }, [onClick]);

  if (loading) {
    return (
      <div className={`bg-white rounded-lg shadow p-6 ${className}`}>
        <div className="animate-pulse">
          {title && <div className="h-6 bg-gray-200 rounded mb-2"></div>}
          {subtitle && <div className="h-4 bg-gray-200 rounded mb-4 w-3/4"></div>}
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            <div className="h-4 bg-gray-200 rounded w-4/6"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-white rounded-lg shadow p-6 border-l-4 border-red-500 ${className}`}>
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Erro</h3>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`
        bg-white rounded-lg shadow p-6 transition-all duration-200
        ${onClick ? 'cursor-pointer' : ''}
        ${hoverable ? 'hover:shadow-lg hover:-translate-y-1' : ''}
        ${className}
      `}
      onClick={handleClick}
    >
      {(title || subtitle || actions) && (
        <div className="flex items-start justify-between mb-4">
          <div>
            {title && <h3 className="text-lg font-semibold text-gray-900">{title}</h3>}
            {subtitle && <p className="text-sm text-gray-600 mt-1">{subtitle}</p>}
          </div>
          {actions && <div className="flex-shrink-0">{actions}</div>}
        </div>
      )}
      {children}
    </div>
  );
});

OptimizedCard.displayName = 'OptimizedCard';

/**
 * Botão otimizado com estados de loading
 */
export const OptimizedButton = memo(({
  children,
  onClick,
  loading = false,
  disabled = false,
  variant = 'primary',
  size = 'md',
  type = 'button',
  className = '',
  icon,
  fullWidth = false
}: OptimizedButtonProps) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleClick = useCallback(async () => {
    if (loading || disabled || isLoading) return;

    try {
      setIsLoading(true);
      await onClick?.();
    } finally {
      setIsLoading(false);
    }
  }, [onClick, loading, disabled, isLoading]);

  const variantClasses = {
    primary: 'bg-blue-600 hover:bg-blue-700 text-white border-transparent',
    secondary: 'bg-gray-200 hover:bg-gray-300 text-gray-900 border-gray-300',
    danger: 'bg-red-600 hover:bg-red-700 text-white border-transparent',
    success: 'bg-green-600 hover:bg-green-700 text-white border-transparent',
    warning: 'bg-yellow-600 hover:bg-yellow-700 text-white border-transparent'
  };

  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base'
  };

  const isDisabled = disabled || loading || isLoading;

  return (
    <button
      type={type}
      onClick={handleClick}
      disabled={isDisabled}
      className={`
        inline-flex items-center justify-center border rounded-md font-medium
        transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
        ${variantClasses[variant]}
        ${sizeClasses[size]}
        ${fullWidth ? 'w-full' : ''}
        ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md'}
        ${className}
      `}
    >
      {(loading || isLoading) ? (
        <>
          <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Carregando...
        </>
      ) : (
        <>
          {icon && <span className="mr-2">{icon}</span>}
          {children}
        </>
      )}
    </button>
  );
});

OptimizedButton.displayName = 'OptimizedButton';

/**
 * Input otimizado com debounce e validação
 */
export const OptimizedInput = memo(({
  label,
  value,
  onChange,
  onBlur,
  placeholder,
  type = 'text',
  required = false,
  disabled = false,
  error,
  helperText,
  className = '',
  autoComplete,
  maxLength,
  debounceMs = 300
}: OptimizedInputProps) => {
  const [internalValue, setInternalValue] = useState(value);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInternalValue(newValue);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      onChange(newValue);
    }, debounceMs);
  }, [onChange, debounceMs]);

  const handleBlur = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    onChange(internalValue);
    onBlur?.();
  }, [onChange, onBlur, internalValue]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <input
        type={type}
        value={internalValue}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete={autoComplete}
        maxLength={maxLength}
        className={`
          block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400
          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
          transition-colors duration-200
          ${error ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'}
          ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}
        `}
      />
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
      {helperText && !error && (
        <p className="mt-1 text-sm text-gray-500">{helperText}</p>
      )}
    </div>
  );
});

OptimizedInput.displayName = 'OptimizedInput';

/**
 * Hook para otimização de re-renders
 */
export const useOptimizedCallback = (
  callback: (...args: any[]) => void | Promise<void>,
  deps: React.DependencyList
) => {
  return useCallback(callback, deps);
};

/**
 * Hook para memoização de valores computados
 */
export const useOptimizedMemo = (
  factory: () => any,
  deps: React.DependencyList
) => {
  return useMemo(factory, deps);
};

/**
 * Hook para debounce de valores
 */
export const useDebounce = (value: any, delay: number): any => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

/**
 * Hook para controle de loading states
 */
export const useAsyncOperation = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const execute = useCallback(async <T,>(operation: () => Promise<T>): Promise<T | null> => {
    try {
      setLoading(true);
      setError(null);
      const result = await operation();
      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido';
      setError(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setLoading(false);
    setError(null);
  }, []);

  return { loading, error, execute, reset };
};

export const LoadingSkeleton = ({ lines = 1, height = 20, className = '' }: { lines?: number; height?: number; className?: string }) => (
  <div className={`animate-pulse ${className}`}>
    {Array.from({ length: lines }).map((_, i) => (
      <div key={i} className="bg-gray-200 rounded mb-2" style={{ height }} />
    ))}
  </div>
);

export default {
  OptimizedTable,
  OptimizedCard,
  OptimizedButton,
  OptimizedInput,
  useOptimizedCallback,
  useOptimizedMemo,
  useDebounce,
  useAsyncOperation,
  LoadingSkeleton
};
